import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DashboardCard, TableChip } from '../../components/PremiumDesign';
import { Button, ConfirmDeleteModal, SkeletonText } from '@/components';
import { V2Table } from '@/common/components';
import {
  getCRMTasksAndFollowups,
  completeCRMContactTask,
  deleteCRMContactTask,
} from '../api';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getCompanyId, formatDateTime } from '../utils/tasksAndFollowups';
import { CRMTaskModal } from './CRMTaskModal';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import { parseApiError } from '@/common/utils';

export function RecentTasksTable() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['crm-recent-tasks', companyId],
    queryFn: () =>
      getCRMTasksAndFollowups(companyId, {
        type: 'task',
        page: 1,
        page_size: 5,
      }),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  const tasks = data?.results || [];

  // Complete Task Mutation
  const completeTaskMutation = useMutation({
    mutationFn: ({ contactId, taskId }) =>
      completeCRMContactTask({ companyId, contactId, taskId }),
    onSuccess: () => {
      toast.success('Task status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update task status.');
    },
  });

  // Delete Task Mutation
  const deleteTaskMutation = useMutation({
    mutationFn: ({ contactId, taskId }) =>
      deleteCRMContactTask({ companyId, contactId, taskId }),
    onSuccess: () => {
      toast.success('Task deleted successfully!');
      setTaskToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete task.');
    },
  });

  const handleRowClick = (item) => {
    if (item.contact?.id) {
      const contactId = String(item.contact.id);
      const contactName = item.contact.name || '';
      try {
        sessionStorage.setItem('crm-contact-overview-id', contactId);
        sessionStorage.setItem('crm-contact-overview-name', contactName);
        window.dispatchEvent(new Event('crm-contact-overview-updated'));
      } catch {
        // ignore storage errors
      }
      navigate('/crm-contacts/overview?tab=tasks', {
        state: { contactId, contactName, from: '/crm-dashboard' },
      });
    } else {
      navigate('/crm-dashboard/task');
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'sr',
        label: 'Sr No.',
        defaultWidth: 80,
        minWidth: 64,
        resizable: false,
        skeleton: <SkeletonText width="w-8" height="h-4" />,
        cell: (_row, index) => index + 1,
      },
      {
        id: 'customer',
        label: 'Customer',
        defaultWidth: 160,
        minWidth: 110,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) => (
          <span className="truncate block font-medium">
            {item.contact?.name || '-'}
          </span>
        ),
      },
      {
        id: 'phone',
        label: 'Phone',
        defaultWidth: 140,
        minWidth: 100,
        resizable: true,
        skeleton: <SkeletonText width="w-24" height="h-4" />,
        cell: (item) => item.contact?.phone || '-',
      },
      {
        id: 'title',
        label: 'Task Title',
        defaultWidth: 200,
        minWidth: 140,
        resizable: true,
        skeleton: <SkeletonText width="w-40" height="h-4" />,
        cell: (item) => (
          <span className="truncate block">{item.data?.title || '-'}</span>
        ),
      },
      {
        id: 'assigned',
        label: 'Assigned To',
        defaultWidth: 150,
        minWidth: 110,
        resizable: true,
        skeleton: <SkeletonText width="w-24" height="h-4" />,
        cell: (item) =>
          item.data?.assigned_to_employee_name ||
          item.data?.assigned_to_name ||
          'Unassigned',
      },
      {
        id: 'due',
        label: 'Due Date',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) => formatDateTime(item.data?.due_at),
      },
      {
        id: 'completed',
        label: 'Completed Date',
        defaultWidth: 170,
        minWidth: 130,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) =>
          item.data?.completed_at
            ? formatDateTime(item.data.completed_at)
            : '-',
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 130,
        minWidth: 100,
        resizable: true,
        skeleton: (
          <SkeletonText width="w-20" height="h-6" className="rounded-full" />
        ),
        cell: (item) => {
          const done =
            !!item.data?.completed_at || item.data?.status === 'completed';
          return (
            <TableChip tone={done ? 'success' : 'warning'}>
              {done ? 'Completed' : 'Pending'}
            </TableChip>
          );
        },
      },
      {
        id: 'action',
        label: 'Action',
        defaultWidth: 150,
        minWidth: 130,
        resizable: false,
        align: 'center',
        stopPropagation: true,
        skeleton: (
          <SkeletonText width="w-24" height="h-8" className="ml-auto" />
        ),
        cell: (item) => {
          const taskObj = item.data;
          const isCompleted =
            !!taskObj?.completed_at || taskObj?.status === 'completed';
          return (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (item.contact?.id && taskObj?.id && !isCompleted) {
                    completeTaskMutation.mutate({
                      contactId: item.contact.id,
                      taskId: taskObj.id,
                    });
                  }
                }}
                disabled={completeTaskMutation.isPending || isCompleted}
                className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all focus:outline-none shrink-0 ${isCompleted ? 'bg-[#50AB3D] border-[#50AB3D] text-white cursor-default' : 'bg-white border-[#868686] text-transparent hover:border-[#50AB3D] cursor-pointer'}`}
                title={isCompleted ? 'Completed' : 'Mark as completed'}
              >
                {isCompleted && (
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => setTaskToEdit(item)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#FAF6E6] transition-colors focus:outline-none shrink-0 cursor-pointer"
                title="Edit task"
              >
                <img
                  src={editIcon}
                  alt="Edit"
                  className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                />
              </button>
              <button
                type="button"
                onClick={() => setTaskToDelete(item)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#FCEAEC] transition-colors focus:outline-none shrink-0 cursor-pointer"
                title="Delete task"
              >
                <img
                  src={trashIcon}
                  alt="Delete"
                  className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                />
              </button>
            </div>
          );
        },
      },
    ],
    [completeTaskMutation.isPending],
  );

  if (isError && !data) {
    return (
      <DashboardCard className="border border-border-default flex flex-col min-h-0 relative">
        <div className="flex items-center justify-between gap-3 mb-[20px]">
          <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
            Recent Tasks
          </h3>
          <Button
            variant="primary"
            className="shrink-0"
            onClick={() => navigate('/crm-dashboard/task')}
          >
            View all
            <span aria-hidden>→</span>
          </Button>
        </div>
        <div className="w-full p-8 text-center bg-white rounded-xl border border-red-200 flex flex-col items-center justify-center gap-3">
          <p className="text-sm font-medium text-red-600">
            Failed to load recent tasks.
          </p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard className="border border-border-default flex flex-col min-h-0 relative">
      <div className="flex items-center justify-between gap-3 mb-[20px]">
        <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
          Recent Tasks
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            className="shrink-0 h-9"
            onClick={() => navigate('/crm-dashboard/task')}
          >
            View all
            <span aria-hidden>→</span>
          </Button>
        </div>
      </div>

      <V2Table
        bare
        compact
        columns={columns}
        data={tasks.slice(0, 5)}
        rowKey={(row, index) => row.data?.id || `task-${index}`}
        storageKey="crm_recent_tasks_column_widths"
        loading={showSkeleton || (!data && !isError)}
        loadingRowCount={5}
        empty="No tasks found"
        onRowClick={handleRowClick}
      />

      {/* Task Add / Edit Modal */}
      <CRMTaskModal
        isOpen={isAddModalOpen || !!taskToEdit}
        onClose={() => {
          setIsAddModalOpen(false);
          setTaskToEdit(null);
        }}
        initialData={taskToEdit}
        companyId={companyId}
        onSuccess={() => refetch()}
      />

      {/* Confirm Delete Task Modal */}
      <ConfirmDeleteModal
        isOpen={!!taskToDelete}
        onCancel={() => setTaskToDelete(null)}
        onConfirm={() => {
          if (taskToDelete?.contact?.id && taskToDelete?.data?.id) {
            deleteTaskMutation.mutate({
              contactId: taskToDelete.contact.id,
              taskId: taskToDelete.data.id,
            });
          }
        }}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        itemName={taskToDelete?.data?.title || 'this task'}
        isPending={deleteTaskMutation.isPending}
      />
    </DashboardCard>
  );
}
